import React, { useState, useEffect, useRef } from "react";
import {
  Handle,
  Position,
  NodeProps,
  useReactFlow,
  useEdges,
  useNodes,
} from "reactflow";
import { Trash2, FlaskConical, ClipboardList, Database } from "lucide-react";
import { Input } from "@components/ui/input";
import { Button } from "@components/ui/button";
import Confirmation from "@components/common/ConfirmDialog";
import { toast } from "@components/ui/use-toast";
import { getConnectedInputs, getExecutionPath } from "../common/nodeDataFlow";
import { executeWorkflow } from "../createPluginApis";
import { handlerStyle, unwrapJsonData } from "../common/commonData";
import NodeContextMenu, {
  useNodeContextMenu,
  createContextMenuItems,
} from "../common/NodeContextMenu";
import CollapsedNodeView from "../common/NodeHeaderView";
import NodeContentSection from "../common/NodeContentSection";
import CustomSelect from "@components/common/CustomSelect";
import InfoTooltip from "@components/common/InfoTooltip";
import { useQuery } from "react-query";
import { fetchLogMappingFields } from "@pages/plugin/createPluginApi";
import { getNodeBackendType } from "../utils/nodeTransformUtils";
import {
  buildWorkflowPayload,
  handleNodeExecutionResults,
  findDownstreamNodes,
} from "../utils/nodeExecutionUtils";
import PaginatedInputViewer from "../common/PaginatedInputViewer";
import { FullscreenWrapper } from "../common/withFullscreen";
import { FullscreenToggleButton } from "../common/FullscreenToggleButton";
import { ValidatedTitleInput } from "../components/ValidatedTitleInput";
import ResponseViewer from "../common/ResponseViewer";
import { useResizablePanels } from "../hooks/useResizablePanels";
import { PanelDivider } from "../common/PanelDivider";

interface SaveDatalistNodeData {
  label?: string;
  header?: string;
  uid: string;
  title: string;
  description?: string;
  datalistUid?: string;
  datalistTitle?: string;
  cronType?: string;
  groupBy?: string;
  datalistMappings?: DatalistMapping[];
  onDelete?: (id: string) => void;
  onDataChange?: (id: string, updates: any) => void;
  borderColor?: string;
  shadowColor?: string;
}

interface DatalistMapping {
  id: string;
  fieldKey: string;
  fieldPath: string;
  fieldValue: any;
  mappedLsField: string;
  displayName: string;
  columnType: string; // TEXT or INTEGER
  dataType: string; // hash, ip, domain, url, file, email, mac, string, number
  enabled: boolean;
}

interface StdFieldType {
  standard: string;
  id: string;
  label: string;
  value: string;
  display_name?: string;
  category?: string;
  [key: string]: any;
}

const columnTypeOptions = [
  { label: "TEXT", value: "TEXT" },
  { label: "INTEGER", value: "INTEGER" },
];

const dataTypeOptions = [
  { label: "hash", value: "hash" },
  { label: "ip", value: "ip" },
  { label: "domain", value: "domain" },
  { label: "url", value: "url" },
  { label: "file", value: "file" },
  { label: "email", value: "email" },
  { label: "mac", value: "mac" },
  { label: "string", value: "string" },
  { label: "number", value: "number" },
];

const EXCLUDED_FIELD_VALUES = new Set([
  "Time",
  "TimeEpoch",
  "time",
  "timeepoch",
]);

const sortedDataTypeOptions = [...dataTypeOptions].sort((a, b) =>
  a.label.localeCompare(b.label),
);

// Keys excluded when building display data from a config-style input
const DISPLAY_DATA_EXCLUDE = [
  "uid",
  "isDecript",
  "isDecrypted",
  "title",
  "testConnectionBtn",
  "tabId",
  "configType",
  "onDelete",
  "onDataChange",
  "header",
  "borderColor",
  "shadowColor",
  "label",
  "nodeType",
  "itemCount",
];

// Helper function to extract json_data from backend output format
const extractJsonData = (data: any) => {
  if (Array.isArray(data)) {
    return data.map((item) => {
      if (item && typeof item === "object" && "json_data" in item) {
        return item.json_data;
      }
      return item;
    });
  }
  if (data && typeof data === "object" && "json_data" in data) {
    return data.json_data;
  }
  return data;
};

// Extract and merge input data from a single connected input source.
// Pulled out of the component so it doesn't get recreated every render.
const getDisplayData = (input: any) => {
  if (input.outputData) {
    return extractJsonData(input.outputData);
  }

  if (input.response) {
    return extractJsonData(input.response);
  }

  const displayData: any = {};
  if (input.configData) {
    const encryptedFields = input.configData.isDecript || [];

    Object.entries(input.configData)
      .filter(([key]) => !DISPLAY_DATA_EXCLUDE.includes(key))
      .forEach(([key, value]) => {
        if (Array.isArray(encryptedFields) && encryptedFields.includes(key)) {
          displayData[key] = "********************";
        } else {
          displayData[key] = value;
        }
      });
  }
  return displayData;
};

// Generate field mappings from an object structure
const generateMappingsFromObject = (
  obj: any,
  prefix: string,
): DatalistMapping[] => {
  const mappings: DatalistMapping[] = [];
  const timestamp = Date.now();
  let index = 0;

  const processValue = (key: string, value: any, currentPrefix: string) => {
    const fieldPath = currentPrefix ? `${currentPrefix}.${key}` : key;
    const fullPath = `$json.${fieldPath}`;

    if (Array.isArray(value)) {
      mappings.push({
        id: `${timestamp}_${index++}`,
        fieldKey: fieldPath,
        fieldPath: fullPath,
        fieldValue: JSON.stringify(value),
        mappedLsField: "",
        displayName: "",
        columnType: "",
        dataType: "",
        enabled: true,
      });
    } else if (typeof value === "object" && value !== null) {
      Object.entries(value).forEach(([nestedKey, nestedValue]) => {
        processValue(nestedKey, nestedValue, fieldPath);
      });
    } else {
      mappings.push({
        id: `${timestamp}_${index++}`,
        fieldKey: fieldPath,
        fieldPath: fullPath,
        fieldValue: value,
        mappedLsField: "",
        displayName: "",
        columnType: "",
        dataType: "",
        enabled: true,
      });
    }
  };

  Object.entries(obj).forEach(([key, value]) => {
    processValue(key, value, prefix);
  });

  return mappings;
};

// Render value for display — pure function, defined once at module scope
const renderValue = (value: any): React.ReactNode => {
  if (value === null || value === undefined)
    return <span className="text-muted-foreground italic">null</span>;

  if (Array.isArray(value)) {
    return <span className="text-xs text-blue-600">Array[{value.length}]</span>;
  }

  if (typeof value === "object") {
    return (
      <span className="text-xs text-purple-600">
        Object ({Object.keys(value).length} keys)
      </span>
    );
  }

  const strValue = String(value);
  return (
    <span className="text-xs">
      {strValue.length > 50 ? strValue.substring(0, 50) + "..." : strValue}
    </span>
  );
};

interface MappingRowProps {
  mapping: DatalistMapping;
  availableFields: any[];
  onRemove: (id: string) => void;
  onLsFieldChange: (id: string, value: string | undefined) => void;
  onUpdate: (id: string, updates: Partial<DatalistMapping>) => void;
}

const MappingRow = React.memo(
  ({
    mapping,
    availableFields,
    onRemove,
    onLsFieldChange,
    onUpdate,
  }: MappingRowProps) => {
    return (
      <div className="border shadow-md rounded-md bg-muted/20 space-y-2 p-3">
        {/* Field Info */}
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <div className="text-xs font-semibold truncate text-primary">
              {mapping.fieldKey}
            </div>
            <div className="text-[10px] text-muted-foreground mt-1">
              Value:{" "}
              <span className="text-primaryBlue">
                {renderValue(mapping.fieldValue)}
              </span>
            </div>
          </div>
          <Trash2
            size={14}
            className="cursor-pointer text-primary hover:text-primary/80 flex-shrink-0"
            onClick={() => onRemove(mapping.id)}
          />
        </div>

        {/* LinkShadow Field Mapping */}
        <div className="space-y-1">
          <label className="text-[10px] font-medium text-muted-foreground flex items-center gap-1">
            LinkShadow Field <span className="text-primary">*</span>
          </label>
          <CustomSelect
            key={`${mapping.id}-${mapping.mappedLsField}`}
            data={availableFields}
            placeholder="Search or scroll to select field..."
            defaultValue={mapping.mappedLsField ? [mapping.mappedLsField] : []}
            sorted={true}
            multiSelect={false}
            onSelect={(selected?: string) =>
              onLsFieldChange(mapping.id, selected)
            }
          />
        </div>

        {/* Column Type */}
        <div className="space-y-1">
          <label className="text-[10px] font-medium text-muted-foreground flex items-center gap-1">
            Column Type <span className="text-primary">*</span>
            <InfoTooltip text="Datalist column type (TEXT for strings, INTEGER for numbers)" />
          </label>
          <CustomSelect
            key={`${mapping.id}-columnType-${mapping.columnType}`}
            data={columnTypeOptions}
            placeholder="Select column type"
            defaultValue={mapping.columnType ? [mapping.columnType] : []}
            sorted={false}
            multiSelect={false}
            onSelect={(selected?: string) =>
              onUpdate(mapping.id, { columnType: selected || "" })
            }
          />
        </div>

        {/* Data Type */}
        <div className="space-y-1">
          <label className="text-[10px] font-medium text-muted-foreground flex items-center gap-1">
            Data Type <span className="text-primary">*</span>
            <InfoTooltip text="Semantic data type (hash, ip, domain, url, etc.)" />
          </label>
          <CustomSelect
            key={`${mapping.id}-dataType-${mapping.dataType}`}
            data={sortedDataTypeOptions}
            placeholder="Select data type"
            defaultValue={mapping.dataType ? [mapping.dataType] : []}
            sorted={false}
            multiSelect={false}
            onSelect={(selected?: string) =>
              onUpdate(mapping.id, { dataType: selected || "" })
            }
          />
        </div>
      </div>
    );
  },
);
MappingRow.displayName = "MappingRow";

const SaveDatalistNode: React.FC<NodeProps<SaveDatalistNodeData>> = ({
  id,
  data,
}) => {
  const label = "Datalist";

  // Node-level state
  const [nodeUid, setNodeUid] = useState(data.uid || "");
  const [nodeTitle, setNodeTitle] = useState(data.title || "");
  const [nodeDescription, setNodeDescription] = useState(
    data.description || "",
  );

  const prevConnectedDataRef = useRef<Map<string, any>>(new Map());

  // Datalist-level state
  const [datalistUid, setDatalistUid] = useState(
    (data as any).datalistUid || "",
  );
  const [datalistTitle, setDatalistTitle] = useState(
    (data as any).datalistTitle || "",
  );
  const [cronType, setCronType] = useState(
    (data as any).cronType || "run_once",
  );
  const [groupBy, setGroupBy] = useState((data as any).groupBy || "hash");

  const [isExpanded, setIsExpanded] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [isTitleValid, setIsTitleValid] = useState(true);

  // Context menu
  const {
    showContextMenu,
    contextMenuPosition,
    handleContextMenu,
    closeContextMenu,
  } = useNodeContextMenu(id);

  // Datalist mappings state
  const [datalistMappings, setDatalistMappings] = useState<DatalistMapping[]>(
    data.datalistMappings || [],
  );

  // Standard fields from API
  const [allStandardFields, setAllStandardFields] = useState<StdFieldType[]>(
    [],
  );

  // Input/Output state
  const [inputData, setInputData] = useState<any[]>([]);
  const [outputData, setOutputData] = useState<any>(null);
  const [errorResponse, setErrorResponse] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  const reactFlowInstance = useReactFlow();
  const { containerRef, widths, startDrag } = useResizablePanels([
    33.33, 33.33, 33.34,
  ]);

  // Ref to track previous outputData and prevent unnecessary updates
  const prevOutputDataRef = useRef<any>(null);

  // Ref to track previous outputData for sync effect (prevent circular dependency)
  const prevSyncOutputDataRef = useRef<any>(null);

  // Fetch log mapping fields
  const { data: logMappingData } = useQuery(
    ["fetchLogMappingFields"],
    ({ signal }) => fetchLogMappingFields(signal),
    {
      enabled: true,
    },
  );

  // Populate allStandardFields from API data
  useEffect(() => {
    if (logMappingData?.data?.standard) {
      const fields = Object.entries(logMappingData.data.standard).map(
        ([key, value]: [string, any], index: number) => ({
          standard: key,
          value: key,
          display_name: value?.display_name ?? "",
          label: key,
          category: value?.category ?? "",
          ...(typeof value === "object" && value !== null ? value : {}),
          id: (index + 1).toString(),
        }),
      );

      setAllStandardFields(fields);
    }
  }, [logMappingData]);

  // Get edges and nodes using hooks
  const edges = useEdges();
  const nodes = useNodes();

  // Manual refresh function for InputViewer
  const refreshInputData = React.useCallback(() => {
    const inputs = getConnectedInputs(id, edges, nodes);
    setInputData(inputs);
  }, [id, edges, nodes]);

  // Get input data from connected nodes.
  // Reference-diffs only the connected nodes' signature data instead of
  // JSON.stringify-ing the whole edges/nodes arrays on every canvas change.
  useEffect(() => {
    const connectedIds = new Set<string>();
    edges.forEach((e) => {
      if (e.target === id) connectedIds.add(e.source);
      if (e.source === id) connectedIds.add(e.target);
    });

    let changed = connectedIds.size !== prevConnectedDataRef.current.size;
    const currentMap = new Map<string, any>();

    nodes.forEach((n) => {
      if (!connectedIds.has(n.id)) return;
      const d: any = n.data || {};
      const sig = d.outputData ?? d.response ?? d.configData;
      currentMap.set(n.id, sig);
      if (prevConnectedDataRef.current.get(n.id) !== sig) changed = true;
    });

    if (changed) {
      prevConnectedDataRef.current = currentMap;
      setInputData(getConnectedInputs(id, edges, nodes));
    }
  }, [id, edges, nodes]);

  // Sync outputData from node data (updated during workflow/node execution)
  // Use stringified comparison to prevent re-renders when content hasn't changed
  useEffect(() => {
    const newOutputData = (data as any).outputData;

    if (!newOutputData) {
      if (outputData !== null && newOutputData === null) {
        setOutputData(null);
      }
      return;
    }

    if (prevOutputDataRef.current === newOutputData) {
      return;
    }

    const newDataStr = JSON.stringify(newOutputData);
    const prevDataStr = prevOutputDataRef.current
      ? JSON.stringify(prevOutputDataRef.current)
      : null;

    if (newDataStr !== prevDataStr) {
      prevOutputDataRef.current = newOutputData;
      setOutputData(newOutputData);
    } else {
      prevOutputDataRef.current = newOutputData;
    }
  }, [(data as any).outputData]);

  // Sync error from node data (set during global workflow execution)
  useEffect(() => {
    const executionStatus = (data as any).executionStatus;
    const error = (data as any).error;

    if (executionStatus === "failed" && error) {
      setErrorResponse(error);
    } else if (executionStatus === "success") {
      setErrorResponse(null);
    }
  }, [(data as any).executionStatus, (data as any).error]);

  // Sync datalistMappings when data.datalistMappings changes (e.g., when loading saved workflow)
  useEffect(() => {
    if (
      data.datalistMappings &&
      Array.isArray(data.datalistMappings) &&
      data.datalistMappings.length > 0
    ) {
      setDatalistMappings(data.datalistMappings);
    }
  }, [data.datalistMappings]);

  // Sync datalist configuration fields when data changes (e.g., when loading saved workflow)
  useEffect(() => {
    if ((data as any).datalistUid) {
      setDatalistUid((data as any).datalistUid);
    }
    if ((data as any).datalistTitle) {
      setDatalistTitle((data as any).datalistTitle);
    }
    if ((data as any).cronType) {
      setCronType((data as any).cronType);
    }
    if ((data as any).groupBy) {
      setGroupBy((data as any).groupBy);
    }
  }, [
    (data as any).datalistUid,
    (data as any).datalistTitle,
    (data as any).cronType,
    (data as any).groupBy,
  ]);

  // Sync data changes with parent
  useEffect(() => {
    prevSyncOutputDataRef.current = outputData;

    const pluginId = sessionStorage.getItem("editPluginId") || "";
    const activeMeshFlowStr = sessionStorage.getItem("activeMeshFlow");
    let requestId = "";
    if (activeMeshFlowStr) {
      try {
        const activeMeshFlow = JSON.parse(activeMeshFlowStr);
        requestId = activeMeshFlow.request_id || "";
      } catch (e) {
        console.error("Failed to parse activeMeshFlow:", e);
      }
    }

    const mappingDataList = datalistMappings
      .filter((m) => m.enabled)
      .map((mapping, index) => ({
        fieldId: String(index + 1),
        rawFieldName: mapping.fieldPath.replace("$json.", ""),
        mappingLsField: mapping.mappedLsField,
        display_name: mapping.displayName,
        column_type: mapping.columnType,
        data_type: mapping.dataType,
      }));

    const storedParameters = {
      datalistUid: datalistUid,
      dataTitile: datalistTitle,
      groupBy: groupBy,
      cronTimeType: cronType,
      typeDatalist: groupBy,
      requestId: requestId,
      pluginId: pluginId,
      mappingDataList: mappingDataList,
      options: {
        batchSize: 1000,
        continueOnError: true,
        validateDataTypes: true,
        disableDotNotation: false,
      },
    };

    data.onDataChange?.(id, {
      uid: nodeUid,
      title: nodeTitle,
      description: nodeDescription,
      datalistUid,
      datalistTitle,
      cronType,
      groupBy,
      datalistMappings,
      outputData,
      storedParameters,
    });
  }, [
    nodeUid,
    nodeTitle,
    nodeDescription,
    datalistUid,
    datalistTitle,
    cronType,
    groupBy,
    datalistMappings,
    outputData,
    id,
  ]);

  // Generate unique Node UID on mount
  useEffect(() => {
    if (!nodeUid) {
      const epoch = Date.now();
      const baseTitle = nodeTitle || "SaveDatalistNode";
      const sanitizedTitle = baseTitle
        .replace(/[^a-zA-Z0-9]/g, "_")
        .toLowerCase();
      setNodeUid(`${sanitizedTitle}_${epoch}`);
    }
  }, []);

  // Generate unique Datalist UID on mount
  useEffect(() => {
    if (!datalistUid) {
      const epoch = Date.now();
      const baseTitle = datalistTitle || "SaveDatalist";
      const sanitizedTitle = baseTitle
        .replace(/[^a-zA-Z0-9]/g, "_")
        .toLowerCase();
      setDatalistUid(`${sanitizedTitle}_datalist_${epoch}`);
    }
  }, []);

  // Auto-populate field mappings when input data changes
  useEffect(() => {
    if (!inputData || inputData.length === 0) {
      return;
    }

    const firstInput = inputData[0];
    const displayData = getDisplayData(firstInput);

    // If mappings already exist (loaded from saved workflow), populate their values
    if (datalistMappings.length > 0 && displayData) {
      let sourceData: any = null;
      if (Array.isArray(displayData) && displayData.length > 0) {
        sourceData = displayData[0];
      } else if (
        typeof displayData === "object" &&
        displayData !== null &&
        !Array.isArray(displayData)
      ) {
        sourceData = displayData;
      }

      if (sourceData) {
        let didChange = false;
        const updatedMappings = datalistMappings.map((mapping) => {
          if (mapping.fieldValue !== null && mapping.fieldValue !== undefined) {
            return mapping;
          }
          const fieldPath = mapping.fieldKey.split(".");
          let value: any = sourceData;
          for (const key of fieldPath) {
            if (value && typeof value === "object" && key in value) {
              value = value[key];
            } else {
              value = null;
              break;
            }
          }
          const newValue = Array.isArray(value) ? JSON.stringify(value) : value;
          if (newValue !== mapping.fieldValue) {
            didChange = true;
            return { ...mapping, fieldValue: newValue };
          }
          return mapping;
        });

        // FIX: this call was previously missing — without it, field values
        // for existing/loaded mappings never actually populate.
        if (didChange) {
          setDatalistMappings(updatedMappings);
        }
      }
      return;
    }

    if (datalistMappings.length === 0 && displayData) {
      let autoMappings: DatalistMapping[] = [];

      if (Array.isArray(displayData) && displayData.length > 0) {
        const firstItem = displayData[0];
        if (typeof firstItem === "object" && firstItem !== null) {
          autoMappings = generateMappingsFromObject(firstItem, "");
        }
      } else if (
        typeof displayData === "object" &&
        displayData !== null &&
        !Array.isArray(displayData)
      ) {
        autoMappings = generateMappingsFromObject(displayData, "");
      }

      if (autoMappings.length > 0) {
        setDatalistMappings(autoMappings);
      }
    }
  }, [inputData, datalistMappings]);

  // Handle delete node
  const handleDelete = (nodeId: string) => {
    setDeleteId(nodeId);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = () => {
    if (deleteId && data.onDelete) {
      data.onDelete(deleteId);
    }
    setShowDeleteConfirm(false);
    setDeleteId(null);
  };

  // Remove a mapping
  const removeMapping = React.useCallback((mappingId: string) => {
    setDatalistMappings((prev) => prev.filter((m) => m.id !== mappingId));
  }, []);

  // Update mapping field
  const updateMapping = React.useCallback(
    (mappingId: string, updates: Partial<DatalistMapping>) => {
      setDatalistMappings((prev) =>
        prev.map((m) => (m.id === mappingId ? { ...m, ...updates } : m)),
      );
    },
    [],
  );

  // Handle LinkShadow field selection
  const handleLsFieldChange = React.useCallback(
    (mappingId: string, selectedValue: string | undefined) => {
      if (!selectedValue) {
        updateMapping(mappingId, { mappedLsField: "", displayName: "" });
        return;
      }
      const selectedField = allStandardFields.find(
        (f) => f.value === selectedValue,
      );
      if (selectedField) {
        updateMapping(mappingId, {
          mappedLsField: selectedValue,
          displayName: selectedField.display_name || selectedField.label,
        });
      }
    },
    [allStandardFields, updateMapping],
  );

  // Normalize + exclude standard fields once whenever the source list changes
  const normalizedStandardFields = React.useMemo(
    () =>
      allStandardFields
        .filter((field) => !EXCLUDED_FIELD_VALUES.has(field.value))
        .map((field) => ({
          ...field,
          label: field.display_name || field.label || field.value,
        })),
    [allStandardFields],
  );

  // O(mappings + mappings*fields) instead of the old O(mappings^2 * fields)
  const availableFieldsMap = React.useMemo(() => {
    const usedByMappingId = new Map<string, string>();
    datalistMappings.forEach((m) => {
      if (m.mappedLsField) usedByMappingId.set(m.mappedLsField, m.id);
    });

    const map: Record<string, any[]> = {};
    datalistMappings.forEach((mapping) => {
      map[mapping.id] = normalizedStandardFields.filter((field) => {
        const usedBy = usedByMappingId.get(field.value);
        return !usedBy || usedBy === mapping.id;
      });
    });
    return map;
  }, [datalistMappings, normalizedStandardFields]);

  // Copy output to clipboard
  const handleCopyOutput = () => {
    const dataToCopy = errorResponse || outputData;
    if (!dataToCopy) return;
    navigator.clipboard.writeText(JSON.stringify(dataToCopy, null, 2));
    toast({
      variant: "success",
      title: "Copied!",
      description: errorResponse
        ? "Error copied to clipboard."
        : "Output copied to clipboard.",
      duration: 2000,
    });
  };

  // Build and test the save datalist configuration
  const testSaveDatalist = async () => {
    const enabledMappings = datalistMappings.filter(
      (m) =>
        m.enabled &&
        m.mappedLsField.trim() &&
        m.columnType.trim() &&
        m.dataType.trim(),
    );

    if (!inputData?.length) {
      toast({
        variant: "destructive",
        title: "No Input Data",
        description: "Connect a node to provide input data",
      });
      return;
    }

    if (!enabledMappings.length) {
      toast({
        variant: "destructive",
        title: "No Mappings Configured",
        description:
          "Map at least one field with column type and data type configured",
      });
      return;
    }

    if (!datalistTitle.trim()) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Datalist Title is required",
        duration: 3000,
      });
      return;
    }

    if (/\s/.test(datalistTitle)) {
      toast({
        variant: "destructive",
        title: "Validation Error",
        description:
          "Datalist Title cannot contain spaces. Use underscores instead.",
        duration: 3000,
      });
      return;
    }

    if (!cronType || !cronType.trim()) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Cron Type is required",
        duration: 3000,
      });
      return;
    }

    if (!groupBy || !groupBy.trim()) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Group By is required",
        duration: 3000,
      });
      return;
    }

    const incomplete = enabledMappings.find(
      (m) => !m.columnType || !m.dataType,
    );
    if (incomplete) {
      toast({
        variant: "destructive",
        title: "Validation Error",
        description: `Please set column type and data type for "${incomplete.fieldKey}"`,
        duration: 3000,
      });
      return;
    }

    const activeMeshFlowStr = sessionStorage.getItem("activeMeshFlow");
    if (!activeMeshFlowStr) {
      toast({
        variant: "destructive",
        title: "Error",
        description:
          "No active mesh flow. Please create or select a mesh flow first.",
        duration: 4000,
      });
      return;
    }

    setOutputData(null);
    setLoading(true);

    try {
      const activeMeshFlow = JSON.parse(activeMeshFlowStr);

      const allNodes = reactFlowInstance.getNodes();
      const allEdges = reactFlowInstance.getEdges();

      const executableNodes = getExecutionPath(id, allNodes, allEdges);

      // Mark all executable nodes as pending so loading border is visible.
      reactFlowInstance.setNodes((nodes) =>
        nodes.map((node) => {
          const isExecutable = executableNodes.some(
            (n: any) => n.id === node.id,
          );
          if (!isExecutable) return node;

          return {
            ...node,
            data: {
              ...node.data,
              executionStatus: "pending",
              executionResult: undefined,
              error: undefined,
            },
          };
        }),
      );

      // Mark all executable nodes as pending so loading border is visible.
      reactFlowInstance.setNodes((nodes) =>
        nodes.map((node) => {
          const isExecutable = executableNodes.some(
            (n: any) => n.id === node.id,
          );
          if (!isExecutable) return node;

          return {
            ...node,
            data: {
              ...node.data,
              executionStatus: "pending",
              executionResult: undefined,
              error: undefined,
            },
          };
        }),
      );

      // Get plugin ID for storedParameters

      const pluginId = sessionStorage.getItem("editPluginId") || "";

      const workflowNodes = executableNodes.map((node) => {
        let nodeParameters: any;

        if (node.id === id && node.type === "savedatalist") {
          const mappingDataList = datalistMappings
            .filter(
              (m) =>
                m.enabled &&
                m.mappedLsField.trim() &&
                m.columnType.trim() &&
                m.dataType.trim(),
            )
            .map((mapping, index) => ({
              fieldId: String(index + 1),
              rawFieldName: mapping.fieldPath.replace("$json.", ""),
              mappingLsField: mapping.mappedLsField,
              display_name: mapping.displayName,
              column_type: mapping.columnType,
              data_type: mapping.dataType,
            }));

          nodeParameters = {
            datalistUid: datalistUid,
            dataTitile: datalistTitle,
            groupBy: groupBy,
            cronTimeType: cronType,
            typeDatalist: groupBy,
            requestId: activeMeshFlow.request_id || "",
            pluginId: pluginId,
            mappingDataList: mappingDataList,
            options: {
              batchSize: 1000,
              continueOnError: true,
              validateDataTypes: true,
              disableDotNotation: false,
            },
          };
        } else {
          nodeParameters =
            node.data.storedParameters || node.data.parameters || {};
        }

        const backendTypeInfo = getNodeBackendType(node.type || "custom");
        let nodeType = backendTypeInfo.type;
        let subType = backendTypeInfo.sub_type;

        if (node.type === "requestBlock") {
          nodeParameters =
            node.data.storedParameters || node.data.parameters || {};
        } else if (node.type === "ifElseNode") {
          nodeParameters = {
            conditions: node.data.conditions || {
              options: {
                caseSensitive: true,
                leftValue: "",
                typeValidation: "strict",
                version: 3,
              },
              conditions: [],
              combinator: "and",
            },
            looseTypeValidation: node.data.looseTypeValidation || false,
            options: node.data.options || {},
          };
        } else if (node.type === "splitOutNode") {
          const splitOutFields = (node.data.fieldsToSplitOut?.[0] || "")
            .split(",")
            .map((f: string) => f.trim())
            .filter((f: string) => f.length > 0);

          const nodeOptions = {
            disableDotNotation: node.data.options?.disableDotNotation ?? false,
            destinationFieldName:
              node.data.options?.destinationFieldName?.trim() ?? "",
          };

          nodeParameters = {
            fieldToSplitOut: splitOutFields.join(","),
            include: node.data.include || "noOtherFields",
            options: nodeOptions,
          } as any;

          if (node.data.include === "selectedOtherFields") {
            if (node.data.fieldsToInclude?.[0]) {
              const includeFields = node.data.fieldsToInclude[0]
                .split(",")
                .map((f: string) => f.trim())
                .filter((f: string) => f.length > 0);
              (nodeParameters as any).fieldsToInclude =
                includeFields.length > 0 ? includeFields.join(",") : "";
            } else {
              (nodeParameters as any).fieldsToInclude = "";
            }
          }
        } else if (
          node.type === "editFields" ||
          node.type === "editFieldsNode"
        ) {
          nodeParameters =
            node.data.storedParameters || node.data.parameters || {};
        } else if (node.type === "startNode") {
          nodeParameters = {};
        } else if (node.type === "savelogs") {
          nodeParameters =
            node.data.storedParameters || node.data.parameters || {};
        } else if (node.type === "savedatalist") {
          nodeParameters =
            node.data.storedParameters || node.data.parameters || {};
        } else if (
          node.type === "authentication" ||
          node.type === "configurationNode" ||
          node.data.configData
        ) {
          const configData = node.data.configData || {};
          nodeParameters = {
            TabId: configData.tabId || configData.TabId,
            Uid: configData.uid || configData.Uid,
            pluginId: pluginId,
            configType: configData.configType || configData.type || "temp",
            configData: {
              ...configData,
            },
          };
        } else {
          nodeParameters =
            node.data.storedParameters || node.data.parameters || {};
          if (node.data.sub_type) {
            subType = node.data.sub_type;
            nodeType = node.data.type || "core";
          }
        }

        return {
          uid: node.data.uid || node.id,
          title: node.data.title || node.data.header || "Untitled",
          description: node.data.description || "",
          type: nodeType,
          sub_type: subType,
          version: "1.0",
          position: [node.position.x, node.position.y],
          parameters: {
            ...nodeParameters,
            label: node.data.label,
          },
        };
      });

      const nodeUidMap: Record<string, string> = {};
      executableNodes.forEach((node) => {
        nodeUidMap[node.id] = node.data.uid || node.id;
      });

      const executionPathNodeIds = new Set(executableNodes.map((n) => n.id));

      const connections: any = {};
      allEdges
        .filter(
          (edge) =>
            executionPathNodeIds.has(edge.source) &&
            executionPathNodeIds.has(edge.target),
        )
        .forEach((edge) => {
          const sourceUid = nodeUidMap[edge.source];
          const targetUid = nodeUidMap[edge.target];

          if (!sourceUid || !targetUid) return;

          if (!connections[sourceUid]) {
            connections[sourceUid] = {
              main: [],
            };
          }

          const sourceNode = executableNodes.find((n) => n.id === edge.source);
          const isIfElseNode =
            sourceNode?.type === "ifElseNode" ||
            sourceNode?.data?.sub_type === "if_else";

          let outputIndex = 0;
          if (isIfElseNode && edge.sourceHandle) {
            outputIndex = edge.sourceHandle === "false" ? 1 : 0;
          }

          if (!connections[sourceUid].main[outputIndex]) {
            connections[sourceUid].main[outputIndex] = [];
          }

          connections[sourceUid].main[outputIndex].push({
            node: targetUid,
            type: "main",
            index: 0,
          });
        });

      const workflowPayload = buildWorkflowPayload(
        activeMeshFlow,
        workflowNodes,
        connections,
      );

      const response = await executeWorkflow(workflowPayload);

      if (response?.results?.[0]?.node_results) {
        const nodeResults = response.results[0].node_results;

        const { hasErrors, errorMessage, errorNodeTitle } =
          handleNodeExecutionResults(reactFlowInstance, nodeResults, allEdges);

        const resultsMap = new Map<string, any>();
        nodeResults.forEach((nodeResult: any) => {
          resultsMap.set(nodeResult.uid, nodeResult);
        });

        const currentNodeResult = resultsMap.get(nodeUid || id);
        const responseData = currentNodeResult
          ? unwrapJsonData(currentNodeResult.output_data)
          : null;

        setOutputData(responseData);

        if (hasErrors) {
          const errorDescription = errorMessage
            ? `${errorNodeTitle}: ${errorMessage}`
            : "One or more nodes failed during execution";

          const shortError =
            errorDescription.length > 150
              ? errorDescription.substring(0, 150) + "..."
              : errorDescription;

          toast({
            variant: "destructive",
            title: "Execution Failed",
            description: shortError,
            duration: 7000,
          });
        } else {
          toast({
            variant: "success",
            title: "Success",
            description: "Save Datalist executed successfully",
            duration: 3000,
          });
        }
      } else {
        const isSuccess =
          response?.success === true ||
          response?.status === "success" ||
          response?.body?.status === 1 ||
          response?.body?.status === "1" ||
          response?.status === 1;

        if (isSuccess) {
          reactFlowInstance.setNodes((nodes) =>
            nodes.map((node) => {
              if (node.id === id) {
                return {
                  ...node,
                  data: {
                    ...node.data,
                    executionStatus: "success",
                  },
                };
              }
              return node;
            }),
          );

          toast({
            variant: "success",
            title: "Success",
            description:
              response?.message || "Save Datalist executed successfully",
            duration: 3000,
          });
        } else {
          const allEdges2 = reactFlowInstance.getEdges();
          const downstreamNodes = findDownstreamNodes(id, allEdges2);

          reactFlowInstance.setNodes((nodes) =>
            nodes.map((node) => {
              if (node.id === id) {
                return {
                  ...node,
                  data: {
                    ...node.data,
                    executionStatus: "failed",
                    error:
                      response?.body?.message ||
                      response?.message ||
                      "Execution failed",
                  },
                };
              } else if (downstreamNodes.has(node.id)) {
                return {
                  ...node,
                  data: {
                    ...node.data,
                    executionStatus: "failed",
                    error: "Not executed due to upstream failure",
                  },
                };
              }
              return node;
            }),
          );

          toast({
            variant: "destructive",
            title: "Execution Failed",
            description:
              response?.body?.message ||
              response?.message ||
              "Save Datalist execution failed",
            duration: 4000,
          });
        }
      }
    } catch (error: any) {
      console.error("Save Datalist Execution Error:", error);

      const allEdges = reactFlowInstance.getEdges();
      const downstreamNodes = findDownstreamNodes(id, allEdges);

      reactFlowInstance.setNodes((nodes) =>
        nodes.map((node) => {
          if (node.id === id) {
            return {
              ...node,
              data: {
                ...node.data,
                executionStatus: "failed",
                error: error?.message || "Save Datalist execution failed",
              },
            };
          } else if (downstreamNodes.has(node.id)) {
            return {
              ...node,
              data: {
                ...node.data,
                executionStatus: "failed",
                error: "Not executed due to upstream failure",
              },
            };
          }
          return node;
        }),
      );

      toast({
        variant: "destructive",
        title: "Error",
        description: error?.message || "Save Datalist execution failed",
        duration: 4000,
      });
    } finally {
      setLoading(false);
    }
  };

  // Memoized input sources for PaginatedInputViewer — avoids rebuilding
  // (and re-running getDisplayData over every input) on every render.
  const inputSources = React.useMemo(() => {
    if (!inputData?.length) return [];
    return inputData.map((input, index) => {
      let displayLabel = input.label || `Source ${index + 1}`;
      if (displayLabel.toLowerCase().includes("switch")) {
        displayLabel = `Rule ${index + 1}`;
      }
      return {
        label: displayLabel,
        data: getDisplayData(input),
        isConfig: !!input.configData,
      };
    });
  }, [inputData]);

  // Render input data section
  const renderInputData = () => {
    if (!inputData?.length) {
      return (
        <div className="text-xs text-muted-foreground p-4 border rounded-md bg-muted/20 text-center">
          <div className="font-medium">No Input Connected</div>
          <div>Connect a node to see data here</div>
        </div>
      );
    }

    return (
      <PaginatedInputViewer
        sources={inputSources}
        onRefresh={refreshInputData}
        sourceCount={inputData.length}
      />
    );
  };

  return (
    <FullscreenWrapper
      id={id}
      isExpanded={isExpanded}
      executionStatus={(data as any).executionStatus}
      borderColor={data.borderColor}
      shadowColor={data.shadowColor}
      onContextMenu={handleContextMenu}
      expandedClassName="w-[90vw] !max-w-[1000px] flex flex-col"
    >
      <Confirmation
        text="Are you sure you want to delete this node?"
        confirmAction={confirmDelete}
        rejectAction={() => setShowDeleteConfirm(false)}
        confirmText="Confirm"
        rejectText="Cancel"
        show={showDeleteConfirm}
        closeDialog={() => setShowDeleteConfirm(false)}
      />

      {/* Context Menu */}
      <NodeContextMenu
        show={showContextMenu}
        position={contextMenuPosition}
        onClose={closeContextMenu}
        items={[
          createContextMenuItems.expandCollapse(isExpanded, () =>
            setIsExpanded((prev) => !prev),
          ),
          createContextMenuItems.delete(
            () => handleDelete(id),
            !!data.onDelete,
          ),
        ]}
      />

      {/* Handles */}
      <Handle type="target" position={Position.Left} className={handlerStyle} />

      {/* Header */}
      <div
        className={`flex items-center justify-between gap-4 rounded-md transition-[padding] duration-500 ${
          isExpanded ? " p-3" : "!p-3"
        }`}
      >
        <CollapsedNodeView
          icon={<ClipboardList />}
          label={label}
          title={nodeTitle}
          iconSize={35}
          borderColor={data.borderColor}
          onToggle={() => setIsExpanded((prev) => !prev)}
          isExpanded={isExpanded}
          executionStatus={(data as any).executionStatus}
          description={nodeDescription}
        />
        <FullscreenToggleButton isExpanded={isExpanded} />
      </div>

      {/* Node Details Section */}
      {isExpanded && (
        <div className="bg-muted/30 bg-muted">
          <div className="mt-2 pt-4 px-4 border-t flex items-start gap-3">
            <div className="w-2/4">
              <label className="text-xs font-medium text-muted-foreground">
                Node Title <span className="text-primary">*</span>
              </label>
              <ValidatedTitleInput
                id={id}
                title={nodeTitle}
                onChange={setNodeTitle}
                nodes={nodes}
                dataTitle={data.title}
                onValidationChange={setIsTitleValid}
              />
            </div>
            <div className="space-y-1 w-2/4">
              <label className="text-xs font-medium text-muted-foreground">
                Node Description (optional)
              </label>
              <textarea
                value={nodeDescription}
                onChange={(e) => setNodeDescription(e.target.value)}
                placeholder="Enter node description"
                className="w-full p-2 border rounded text-xs resize-none text-muted-foreground bg-background"
                rows={1}
              />
            </div>
          </div>
        </div>
      )}

      {/* Expanded Content */}
      {isExpanded && (
        <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
          {/* Datalist Configuration Section */}
          <div className="mt-2 p-4 border-t flex items-start gap-4 flex-shrink-0">
            <div className="w-1/3">
              <label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                Datalist Title <span className="text-primary">*</span>
                <InfoTooltip text="Datalist title without spaces (underscores allowed)" />
              </label>
              <Input
                value={datalistTitle}
                onChange={(e) => {
                  const value = e.target.value.replace(/\s+/g, "_");
                  setDatalistTitle(value);
                }}
                placeholder="Enter datalist title (no spaces)"
                className="text-xs mt-1"
                required
              />
            </div>

            <div className="space-y-1 w-1/3">
              <label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                Cron Type <span className="text-primary">*</span>
                <InfoTooltip text="How the datalist should be updated: run once, replace each time, or maintain as time series" />
              </label>
              <CustomSelect
                data={[
                  { label: "Run Once", value: "run_once" },
                  { label: "Replace Each Time", value: "replace_each_time" },
                  { label: "Make Time series", value: "make_timeseries" },
                ]}
                placeholder="Select cron type"
                onSelect={(value) => setCronType(value || "")}
                defaultValue={cronType ? [cronType] : []}
                multiSelect={false}
                sorted={false}
              />
            </div>

            <div className="space-y-1 w-1/3">
              <label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                Group By <span className="text-primary">*</span>
                <InfoTooltip text="Primary field type to group datalist entries by" />
              </label>
              <CustomSelect
                data={[
                  { label: "hash", value: "hash" },
                  { label: "ip", value: "ip" },
                  { label: "domain", value: "domain" },
                  { label: "email", value: "email" },
                ]}
                placeholder="Select group by field"
                onSelect={(value) => setGroupBy(value || "")}
                defaultValue={groupBy ? [groupBy] : []}
                multiSelect={false}
                sorted={false}
              />
            </div>
          </div>

          {/* 3 Section Grid */}
          <div
            ref={containerRef}
            className="flex gap-0 p-4 border-t flex-1 min-h-0 overflow-hidden h-full"
          >
            {/* Left Section - Input Data */}
            <div
              style={{
                width: `${widths[0]}%`,
                minWidth: 80,
                flexShrink: 0,
                height: "100%",
                display: "flex",
                flexDirection: "column",
                minHeight: 0,
                overflow: "hidden",
              }}
            >
              <NodeContentSection hasRelative={true} scrollable={true}>
                {renderInputData()}
              </NodeContentSection>
            </div>

            <PanelDivider onMouseDown={startDrag(0)} />

            {/* Middle Section - Field Mappings */}
            <div
              style={{ width: `${widths[1]}%`, minWidth: 80, flexShrink: 0 }}
            >
              <NodeContentSection hasRelative={true} scrollable={true}>
                <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden flex flex-col gap-2">
                  <h3 className="text-sm font-semibold sticky top-0 bg-background z-10 pb-2 border-b">
                    Parameters
                  </h3>

                  {/* Empty State */}
                  {datalistMappings.length === 0 && (
                    <div className="border-2 border-dashed border-muted-foreground rounded-md p-8 text-center">
                      <Database
                        size={32}
                        className="mx-auto mb-2 text-muted-foreground"
                      />
                      <p className="text-xs text-muted-foreground">
                        Connect an input node to auto-populate field mappings
                      </p>
                    </div>
                  )}

                  {/* Mappings List */}
                  {datalistMappings.length > 0 && (
                    <div
                      className="space-y-2 pt-1"
                      onWheel={(e) => e.stopPropagation()}
                    >
                      {datalistMappings.map((mapping) => (
                        <MappingRow
                          key={mapping.id}
                          mapping={mapping}
                          availableFields={availableFieldsMap[mapping.id] || []}
                          onRemove={removeMapping}
                          onLsFieldChange={handleLsFieldChange}
                          onUpdate={updateMapping}
                        />
                      ))}
                    </div>
                  )}
                </div>
                {/* Execute Button */}
                <div className="sticky -bottom-1 left-0 right-0 bg-background border-t">
                  <Button
                    onClick={testSaveDatalist}
                    disabled={
                      !isTitleValid ||
                      !inputData ||
                      inputData.length === 0 ||
                      !datalistMappings.some(
                        (m) =>
                          m.mappedLsField.trim() &&
                          m.columnType.trim() &&
                          m.dataType.trim(),
                      ) ||
                      !datalistTitle.trim() ||
                      !cronType ||
                      !cronType.trim() ||
                      !groupBy ||
                      !groupBy.trim() ||
                      loading
                    }
                    className="w-full mt-4 sticky bottom-0 left-0 right-0 rounded-b-md"
                    size="sm"
                    title="Execute predecessor nodes and preview datalist creation"
                  >
                    <FlaskConical size={14} className="mr-2" />
                    Execute
                  </Button>
                </div>
              </NodeContentSection>
            </div>

            <PanelDivider onMouseDown={startDrag(1)} />

            {/* Right Section - Output Preview */}
            <div
              style={{ width: `${widths[2]}%`, minWidth: 80, flexShrink: 0 }}
            >
              <NodeContentSection scrollable={false} hasRelative={true}>
                <ResponseViewer
                  response={errorResponse || outputData}
                  loading={loading}
                  emptyMessage="Execute save datalist to see output preview"
                  loadingMessage="Executing..."
                  onCopy={handleCopyOutput}
                  isError={!!errorResponse}
                  isLoopChild={(data as any).is_loop_child || false}
                  totalIterations={(data as any).total_iterations || 0}
                  iterations={(data as any).iterations || []}
                  fillParent={true}
                  onResponseChange={(newData) => {
                    setOutputData(newData);
                    data.onDataChange?.(id, {
                      response: newData,
                      manuallyEdited: true,
                    });
                  }}
                />
              </NodeContentSection>
            </div>
          </div>
        </div>
      )}
    </FullscreenWrapper>
  );
};

export default SaveDatalistNode;
